import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  fetchEvents,
  updateEvent,
  deleteEvent,
} from "../services/eventsService";
import { showConfirmationDialog } from "../../utils/apiHelper";
import LiveQuestionsPanel from "./LiveQuestionsPanel";

// Density Part 4 — Events CMS list. Publish/unpublish inline; the join code
// links to the public landing page hosts print their QR against.

const EventsListLayer = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { ok, text }

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetchEvents();
      if (res.data?.success) setItems(res.data.events || []);
    } catch (err) {
      setMessage({ ok: false, text: "Failed to load events" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const togglePublish = async (event) => {
    const next = event.status === "published" ? "draft" : "published";
    try {
      const res = await updateEvent(event._id, { status: next });
      if (res.data?.success) {
        setMessage({
          ok: true,
          text: next === "published" ? "Event published" : "Event unpublished",
        });
        fetchData();
      }
    } catch (err) {
      setMessage({
        ok: false,
        text:
          err?.response?.data?.message ||
          "Could not change status. Drafts need every field before publishing.",
      });
    }
  };

  const removeEvent = async (event) => {
    const result = await showConfirmationDialog(
      "This permanently deletes the event. Eventbrite RSVPs are unaffected.",
      "Yes, delete it",
      "Cancel"
    );
    if (!result?.isConfirmed) return;
    try {
      const res = await deleteEvent(event._id);
      if (res.data?.success) {
        setMessage({ ok: true, text: "Event deleted" });
        fetchData();
      }
    } catch {
      setMessage({ ok: false, text: "Delete failed" });
    }
  };

  return (
    <div>
    <div className="card basic-data-table">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h5 className="card-title mb-0">Curiouzz Social</h5>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => navigate("/events/add")}
        >
          <Icon icon="ic:baseline-plus" className="me-1" />
          Add Social
        </button>
      </div>
      <div className="card-body">
        {message && (
          <div
            className={`alert ${message.ok ? "alert-success" : "alert-danger"} py-2`}
            role="alert"
          >
            {message.text}
          </div>
        )}
        <div className="table-responsive">
          <table className="table bordered-table mb-0">
            <thead>
              <tr>
                <th>Title</th>
                <th>City</th>
                <th>Date</th>
                <th>Tag</th>
                <th>Status</th>
                <th>Join Code</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>Loading…</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7}>Nothing here yet. Add the first Curiouzz Social.</td>
                </tr>
              ) : (
                items.map((event) => (
                  <tr key={event._id}>
                    <td>{event.title || <em>(untitled draft)</em>}</td>
                    <td>{event.city || "N/A"}</td>
                    <td>
                      {event.eventDateISO
                        ? moment.parseZone(event.eventDateISO).format(
                            "ddd, MMM D · h:mm A"
                          )
                        : "N/A"}
                    </td>
                    <td>{event.tag ? event.tag.replace("_", " ") : "N/A"}</td>
                    <td>
                      <span
                        className={`badge ${
                          event.status === "published"
                            ? "bg-success-focus text-success-main"
                            : "bg-neutral-200 text-neutral-600"
                        } px-12 py-4 radius-4`}
                      >
                        {event.status}
                      </span>
                    </td>
                    <td>
                      <a
                        href={`https://webapi.curiouzz.com/e/${event.joinCode}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Open the public landing page (point your printed QR here)"
                      >
                        {event.joinCode}
                      </a>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-primary btn-sm me-2"
                        onClick={() => togglePublish(event)}
                      >
                        {event.status === "published" ? "Unpublish" : "Publish"}
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm me-2"
                        onClick={() => navigate(`/events/edit/${event._id}`)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeEvent(event)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <LiveQuestionsPanel />
    </div>
  );
};

export default EventsListLayer;
